/**
 * Русские названия ролей в вики (владелец 16.10.2026: «поменяй английские названия везде»).
 *
 * Код роли — общее пространство: роль CRM (ADMIN, PARTNER, menedzher_otdela_zaboty_kts…) и должность
 * iiko (COOK, CASHIER…); в доступе категорий встречаются и префиксные формы `crm:CODE` / `iiko:CODE`.
 * Один резолвер `roleLabel(code, …источники)` на все экраны. Источники по убыванию точности:
 *   1. справочники, которые экран уже загрузил (`GET /wiki/education/role-labels` — роли CRM по их
 *      русскому name + должности iiko; список ролей конструктора — assign-options);
 *   2. запасная карта ниже (если справочник не загрузился);
 *   3. для неизвестного транслит-кода — «человеческий» вид (обратная транслитерация), иначе сам код.
 * Хук с запросом — useRoleLabels.ts (тут только чистые функции, их проверяет тест).
 */

export type RoleLabels = Record<string, string>;

/**
 * Запасные названия на случай, когда справочник с сервера ещё не пришёл. Основная карта должностей
 * iiko — на бэкенде (IIKO_ROLE_LABELS в wiki-course-roles.ts); здесь её зеркало плюс известные
 * коды ролей CRM (названия заданы миграциями ролей и совпадают с серверными).
 */
export const FALLBACK_ROLE_LABELS: Readonly<RoleLabels> = {
  COOK: 'Повар',
  CASHIER: 'Кассир',
  COURIER: 'Курьер',
  MANAGER: 'Управляющий',
  OTHER: 'Прочие',
  MARKETING: 'Маркетинг',
  ADMIN: 'Администратор',
  PARTNER: 'Франчайзи-партнёр',
};

/** Код без префикса источника: `crm:PARTNER` → `PARTNER`, `iiko:COOK` → `COOK`. */
export function bareRoleCode(code: string): string {
  return code.replace(/^(crm|iiko):/i, '').trim();
}

// ── Обратная транслитерация транслит-слагов (последняя надежда) ──────────

/** Длинные сочетания — раньше одиночных букв (жадно). */
const TRANSLIT_RULES: Array<[string, string]> = [
  ['shch', 'щ'], ['sch', 'щ'], ['zh', 'ж'], ['kh', 'х'], ['ts', 'ц'], ['ch', 'ч'], ['sh', 'ш'],
  ['yu', 'ю'], ['ya', 'я'], ['yo', 'ё'], ['ye', 'е'], ['ii', 'ий'], ['yy', 'ый'], ['iy', 'ий'],
  ['a', 'а'], ['b', 'б'], ['v', 'в'], ['g', 'г'], ['d', 'д'], ['e', 'е'], ['z', 'з'], ['i', 'и'],
  ['j', 'й'], ['k', 'к'], ['l', 'л'], ['m', 'м'], ['n', 'н'], ['o', 'о'], ['p', 'п'], ['r', 'р'],
  ['s', 'с'], ['t', 'т'], ['u', 'у'], ['f', 'ф'], ['h', 'х'], ['c', 'к'], ['y', 'ы'], ['q', 'к'],
  ['w', 'в'], ['x', 'кс'],
];

/** Слаг вида `menedzher_otdela_zaboty_kts`: строчная латиница с `_`/`-` либо с русскими сочетаниями. */
function looksLikeTranslitSlug(code: string): boolean {
  if (!/^[a-z][a-z0-9_-]*$/.test(code)) return false;
  return /[_-]/.test(code) || /(zh|kh|sch|shch|yu|ya|ii|yy|iy)/.test(code);
}

/**
 * Читаемый вид неизвестного кода роли. Транслит-слаг (`upravlyayuschii`, `menedzher_otdela_zaboty_kts`)
 * превращаем обратно в русские слова — «Управляющий», «Менеджер отдела заботы кц» (приблизительно:
 * это последняя надежда, когда названия роли нигде нет); любой другой код возвращаем как есть.
 */
export function humanizeRoleCode(code: string): string {
  const bare = bareRoleCode(code);
  if (!looksLikeTranslitSlug(bare)) return bare;
  let out = '';
  const word = bare.replace(/[_-]+/g, ' ');
  for (let i = 0; i < word.length; ) {
    if (word[i] === ' ') { out += ' '; i += 1; continue; }
    const rule = TRANSLIT_RULES.find(([lat]) => word.startsWith(lat, i));
    if (rule) { out += rule[1]; i += rule[0].length; } else { out += word[i]; i += 1; }
  }
  // Слово на «-ii» в конце — это «-ий» уже сделано правилом; первая буква заглавная.
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/**
 * Название роли по коду. `sources` — справочники «код → название» в порядке приоритета (первый, где код
 * найден, побеждает); потом запасная карта; потом humanizeRoleCode.
 */
export function roleLabel(code: string | null | undefined, ...sources: Array<RoleLabels | null | undefined>): string {
  if (!code) return '';
  const bare = bareRoleCode(code);
  if (!bare) return '';
  for (const src of sources) {
    const hit = src?.[code] ?? src?.[bare];
    if (hit) return hit;
  }
  return FALLBACK_ROLE_LABELS[bare] ?? FALLBACK_ROLE_LABELS[bare.toUpperCase()] ?? humanizeRoleCode(bare);
}

/** Список ролей через запятую; пустой список — `empty` («Все», «все роли»). */
export function roleLabelsText(codes: readonly string[] | null | undefined, labels: RoleLabels | null | undefined, empty = 'Все'): string {
  return codes && codes.length > 0 ? codes.map((c) => roleLabel(c, labels)).join(', ') : empty;
}
